import {LinkStatus, ProductField, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {Checkbox} from '@treez-ui/core/forms/Checkbox';
import {ProductTypeIconMap, TreezIcon} from '@treez-ui/core/icons';
import {formatCurrencyForDisplay, getAmountUomString} from '@treez-ui/core/lib/product';
import React from 'react';
import {TableCellProps} from '@material-ui/core';
import {Button, ButtonClass, ProductLinkStatusButton} from '@treez-ui/core/buttons';
import {ProductFieldAttributesToDisplayNameMap} from '@treez/commons/utils/product';
import dayjs from '~/lib/date';

const {AMOUNT, BRAND_NAME, NAME, PRICE, SKU, SUBTYPE, TYPE, UPDATED_AT} = ProductField;

const {LINKED, NOT_LINKED} = LinkStatus;

const baseStyles = {
    height: '40px',
    width: '120px',
    maxHeight: '40px',
};

const iconBaseStyles = {
    ...baseStyles,
    width: '40px',
    maxWidth: '40px',
};

export const generateSelectColumn = (
    selectAll: (isSelected: boolean) => void,
    allSelected: boolean,
    select: (isSelected: boolean, value: any) => void
) => {
    return {
        header: (
            <Checkbox
                checked={allSelected}
                loading={false}
                onChange={(e) => selectAll(e.target.checked)}
            />
        ),
        render: (p: ProductInCatalog, selected?: boolean) => {
            return selected != null ? (
                <Checkbox
                    checked={selected}
                    loading={false}
                    onChange={(e) => select(e.target.checked, p)}
                />
            ) : (
                <div />
            );
        },
        padding: 'none' as TableCellProps['padding'],
        align: 'center' as TableCellProps['align'],
        styles: {
            ...iconBaseStyles,
            '& label': {
                marginRight: '0px',
            },
        },
    };
};

export const generateNameColumn = (
    inspectProduct?: (productToInspect: ProductInCatalog) => void
) => {
    return {
        field: NAME,
        header: 'Name',
        render: (p: ProductInCatalog) => (
            <button
                className="clickable-table-cell-button product-name-cell"
                onClick={() => {
                    if (inspectProduct) {
                        inspectProduct(p);
                    }
                }}
            >
                {p.name}
            </button>
        ),
        styles: {
            ...baseStyles,
            width: '240px',
        },
    };
};

export const priceColumn = {
    header: ProductFieldAttributesToDisplayNameMap[PRICE],
    field: PRICE,
    render: (product: ProductInCatalog) => formatCurrencyForDisplay(String(product.price)),
};

export const typeColumn = {
    header: ProductFieldAttributesToDisplayNameMap[TYPE],
    field: TYPE,
    render: (productToRender: ProductInCatalog) => {
        const iconRenderer = ProductTypeIconMap[productToRender.type];
        return (
            <div className="icon-w-text-container-table ">
                {iconRenderer ? iconRenderer({key: `${productToRender.id}-type-icon`}) : <div />}
                <div> {productToRender.type || ''} </div>
            </div>
        );
    },
    styles: baseStyles,
};

export const lastUpdatedColumn = {
    header: 'Last Updated',
    field: UPDATED_AT,
    render: (productToRender: ProductInCatalog) => {
        if (productToRender.updatedAt) {
            return dayjs(productToRender.updatedAt).fromNow(true);
        }

        return '';
    },
};

export const amountColumn = {
    field: AMOUNT,
    header: ProductFieldAttributesToDisplayNameMap[AMOUNT],
    render: (productToRender: ProductInCatalog) => {
        return getAmountUomString(Number(productToRender.amount), productToRender.uom);
    },
    styles: {
        ...baseStyles,
        width: '80px',
    },
};

export const generateBrandColumn = (
    inspectProduct?: (productToInspect: ProductInCatalog) => void
) => ({
    field: BRAND_NAME,
    header: ProductFieldAttributesToDisplayNameMap[BRAND_NAME],
    render: (productToRender: ProductInCatalog) => (
        <button
            className="clickable-table-cell-button brand-name-cell"
            onClick={() => {
                if (inspectProduct) {
                    inspectProduct(productToRender);
                }
            }}
        >
            {productToRender.brandName}
        </button>
    ),
    styles: {
        ...baseStyles,
        width: '160px',
    },
});

export const subtypeColumn = {
    field: SUBTYPE,
    header: ProductFieldAttributesToDisplayNameMap[SUBTYPE],
    render: (productToRender: ProductInCatalog) => {
        return productToRender.subtype;
    },
    styles: baseStyles,
};

export const skuColumn = {
    header: ProductFieldAttributesToDisplayNameMap[SKU],
    field: SKU,
    render: (productToRender: ProductInCatalog) => {
        return productToRender.sku;
    },
    styles: baseStyles,
};

export const generateLinkActionColumn = (
    linkProductAction: (productToLink: ProductInCatalog) => void
) => ({
    header: 'Link to Brand',
    render: (productToRender: ProductInCatalog) => {
        return (
            <div className="link-product-button-container">
                <Button
                    buttonClass={ButtonClass.QUIET}
                    icon={<TreezIcon />}
                    onClick={() => linkProductAction(productToRender)}
                    text="Link Product"
                />
            </div>
        );
    },
    styles: {
        width: '160px',
    },
});

export const generateLinkStatusColumn = (
    unlinkProductAction: Function,
    linkProductAction: Function
) => ({
    header: '',
    render: (productToRender) => {
        const isLinked: boolean = productToRender.linkedTo != null;

        return (
            <ProductLinkStatusButton
                onClick={() =>
                    isLinked
                        ? unlinkProductAction(productToRender)
                        : linkProductAction(productToRender)
                }
                status={isLinked ? LINKED : NOT_LINKED}
            />
        );
    },
    padding: 'none' as TableCellProps['padding'],
    styles: {
        ...iconBaseStyles,
        padding: '0 4px',
    },
});
